import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { Fragment, useEffect, useState, useMemo } from 'react'
import { ZIndex } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import {
  Button,
  DotLoader,
  Modal,
  DeprecatedSingleSelect,
  Radio,
  toast,
} from '@opallabs/lapidary'
import { DatePicker } from '@opallabs/satellite'
import {
  useLabelSetsFromBackbone,
  useSwimlaneOptions,
  useCollectionOptions,
  usePlansAndBoardsData,
} from './hooks'
import { MigrationToast } from './MigrationToast'
import * as styles from './styles'
import {
  StoryMomentsMigrationModalProps,
  DateFilterType,
  GroupingType,
  PlanOption,
  BoardOption,
  LabelSetOption,
  SwimlaneOption,
  CollectionOption,
} from './types'
import { submitMigration } from './utils'

export const StoryMomentsMigrationModal: React.FC<
  StoryMomentsMigrationModalProps
> = ({ isOpen, storyId, storyName, onCloseModal }) => {
  const [selectedPlan, setSelectedPlan] = useState<PlanOption | null>(null)
  const [selectedBoard, setSelectedBoard] = useState<BoardOption | null>(null)
  const [selectedPlanLabelSet, setSelectedPlanLabelSet] =
    useState<LabelSetOption | null>(null)
  const [selectedBoardLabelSet, setSelectedBoardLabelSet] =
    useState<LabelSetOption | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Date range filtering state
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>('all')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  // Grouping state
  const [planGroupingType, setPlanGroupingType] =
    useState<GroupingType>('story')
  const [boardGroupingType, setBoardGroupingType] =
    useState<GroupingType>('story')

  // Get workspace UUID and story dates from global Backbone state
  const workspaceUuid =
    window.OpalMoments?.State?.current_brand?.get('uuid') ?? null
  const currentStory = window.OpalMoments?.State?.current_story
  const storyStartDate = currentStory?.get('scheduled_start_at') ?? null
  const storyEndDate = currentStory?.get('scheduled_end_at') ?? null

  // Get theme
  const theme = useTheme()

  // Use custom hooks for data fetching
  const { planOptions, boardOptions, isPlansLoading, isBoardsLoading } =
    usePlansAndBoardsData(isOpen, workspaceUuid, storyStartDate, storyEndDate)

  const { labelSets, isLoading: isLabelSetsLoading } =
    useLabelSetsFromBackbone()

  const {
    swimlaneOptions,
    isLoading: isSwimlaneOptionsLoading,
    selectedSwimlane,
    setSelectedSwimlane,
  } = useSwimlaneOptions(selectedPlan, workspaceUuid)

  const {
    collectionOptions,
    isLoading: isCollectionOptionsLoading,
    selectedCollection,
    setSelectedCollection,
  } = useCollectionOptions(selectedBoard)

  // Initialize date range with story dates when modal opens
  useEffect(() => {
    if (isOpen && storyStartDate && storyEndDate) {
      setStartDate(new Date(storyStartDate))
      setEndDate(new Date(storyEndDate))
    }
  }, [isOpen, storyStartDate, storyEndDate])

  const labelSetOptions = labelSets

  const handleMigrate = async () => {
    setIsSubmitting(true)

    try {
      await submitMigration(storyId, {
        migration: {
          plan_id: selectedPlan?.value || null,
          board_id: selectedBoard?.value || null,
          plan_grouping_label_set_id: selectedPlanLabelSet?.value || null,
          board_grouping_label_set_id: selectedBoardLabelSet?.value || null,
          date_filter_type: dateFilterType,
          start_date:
            dateFilterType === 'range' && startDate
              ? startDate.toISOString().split('T')[0]
              : null,
          end_date:
            dateFilterType === 'range' && endDate
              ? endDate.toISOString().split('T')[0]
              : null,
          plan_grouping_type: selectedPlan ? planGroupingType : null,
          board_grouping_type: selectedBoard ? boardGroupingType : null,
          existing_swimlane_id: selectedSwimlane?.value || null,
          existing_collection_id: selectedCollection?.value || null,
        },
      })

      toast(
        (props) => (
          <MigrationToast
            {...props}
            storyName={storyName}
            planTitle={selectedPlan?.plan.title}
            boardTitle={selectedBoard?.board.title}
          />
        ),
        {
          type: 'success',
          toastId: 'migration-toast',
          closeOnClick: true,
          autoClose: 8000,
        }
      )

      onCloseModal()
    } catch (error) {
      console.error('Migration error:', error)
      toast(
        error instanceof Error
          ? error.message
          : 'Failed to start migration. Please try again.',
        {
          type: 'error',
        }
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const isInitialLoading =
    isPlansLoading || isBoardsLoading || isLabelSetsLoading

  // Validation for submit button
  const isSubmitDisabled = useMemo(() => {
    // Basic requirements
    if (!selectedPlan && !selectedBoard) return true
    if (isSubmitting) return true

    // Date range validation
    if (dateFilterType === 'range' && (!startDate || !endDate)) return true
    if (
      dateFilterType === 'range' &&
      startDate &&
      endDate &&
      startDate > endDate
    )
      return true

    // Plan-specific validation
    if (selectedPlan) {
      if (planGroupingType === 'labelset' && !selectedPlanLabelSet) return true
      if (planGroupingType === 'existing' && !selectedSwimlane) return true
    }

    // Board-specific validation
    if (selectedBoard) {
      if (boardGroupingType === 'labelset' && !selectedBoardLabelSet)
        return true
      if (boardGroupingType === 'existing' && !selectedCollection) return true
    }

    return false
  }, [
    selectedPlan,
    selectedBoard,
    isSubmitting,
    dateFilterType,
    startDate,
    endDate,
    planGroupingType,
    selectedPlanLabelSet,
    selectedSwimlane,
    boardGroupingType,
    selectedBoardLabelSet,
    selectedCollection,
  ])

  // Modal rendering
  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onCloseModal()
      }}
    >
      <Modal.Content css={styles.modalContent} scrollable={true}>
        <Modal.Title>Migrate Story Moments</Modal.Title>
        <VisuallyHidden asChild>
          <Modal.Description>
            Migrate all moments from this story to a plan and/or board.
          </Modal.Description>
        </VisuallyHidden>
        <Modal.Body css={styles.modalBody}>
          <Modal.Close onClick={onCloseModal} />

          {isInitialLoading && <DotLoader />}

          {!isInitialLoading && (
            <Fragment>
              <div css={styles.section}>
                <h3 css={styles.sectionTitle}>Moments to Migrate</h3>
                <p css={styles.sectionDescription}>
                  Choose which moments to migrate based on their scheduled
                  dates.
                </p>

                <div css={styles.radioGroup}>
                  <Radio.Group
                    name="dateFilter"
                    theme={theme}
                    defaultValue={dateFilterType}
                    onChange={(e) =>
                      setDateFilterType(e.target.value as DateFilterType)
                    }
                  >
                    <Radio.Input value="all" label="All moments" />
                    <Radio.Input
                      value="range"
                      label="Moments within a date range"
                    />
                  </Radio.Group>
                </div>

                {dateFilterType === 'range' && (
                  <div css={styles.dateRangeSection}>
                    <div css={styles.datePickerRow}>
                      <div css={styles.datePickerContainer}>
                        <span css={styles.datePickerLabel}>Start Date</span>
                        <DatePicker
                          selectedDate={startDate}
                          onDateSelect={(date) =>
                            setStartDate(date?.dateObject || null)
                          }
                          datePickerDisplayType="select"
                          placeholder="Select start date"
                          showButtonText={true}
                        />
                      </div>
                      <div css={styles.datePickerContainer}>
                        <span css={styles.datePickerLabel}>End Date</span>
                        <DatePicker
                          selectedDate={endDate}
                          onDateSelect={(date) =>
                            setEndDate(date?.dateObject || null)
                          }
                          datePickerDisplayType="select"
                          placeholder="Select end date"
                          showButtonText={true}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div css={styles.section}>
                <h3 css={styles.sectionTitle}>Destinations</h3>
                <p css={styles.sectionDescription}>
                  Choose at least one destination for your moments.
                </p>

                <DeprecatedSingleSelect<PlanOption>
                  css={styles.formElement}
                  label="Plan"
                  options={planOptions}
                  menuWidth="calc(100% - 64px)"
                  isSearchable={true}
                  styleOverrides={{
                    zIndex: ZIndex.Shroud.Popover,
                  }}
                  selectedItem={selectedPlan || undefined}
                  onChange={setSelectedPlan}
                  placeholder="Select a plan..."
                />

                {selectedPlan && (
                  <div css={styles.nestedGroupingSection}>
                    <h4 css={styles.nestedGroupingTitle}>Moment Grouping</h4>
                    <div css={styles.radioGroup}>
                      <Radio.Group
                        name="planGrouping"
                        theme={theme}
                        defaultValue={planGroupingType}
                        onChange={(e) =>
                          setPlanGroupingType(e.target.value as GroupingType)
                        }
                      >
                        <Radio.Input
                          value="story"
                          label="Group by story name"
                        />
                        <Radio.Input
                          value="existing"
                          label="Group into existing swimlane"
                        />
                        <Radio.Input
                          value="labelset"
                          label="Group by label set"
                        />
                      </Radio.Group>
                    </div>

                    {planGroupingType === 'existing' && (
                      <div css={styles.subSelection}>
                        {isSwimlaneOptionsLoading ? (
                          <DotLoader />
                        ) : swimlaneOptions.length > 0 ? (
                          <DeprecatedSingleSelect<SwimlaneOption>
                            css={styles.formElement}
                            label="Existing Swimlane"
                            options={swimlaneOptions}
                            menuWidth="calc(100% - 64px)"
                            isSearchable={true}
                            selectedItem={selectedSwimlane || undefined}
                            onChange={setSelectedSwimlane}
                            placeholder="Select an existing swimlane..."
                          />
                        ) : (
                          <p css={styles.subSelectionNote}>
                            No swimlanes found in this plan.
                          </p>
                        )}
                      </div>
                    )}

                    {planGroupingType === 'labelset' && (
                      <div css={styles.subSelection}>
                        <DeprecatedSingleSelect<LabelSetOption>
                          css={styles.formElement}
                          label="Label Set for Plan"
                          options={labelSetOptions}
                          menuWidth="calc(100% - 64px)"
                          isSearchable={true}
                          selectedItem={selectedPlanLabelSet || undefined}
                          onChange={setSelectedPlanLabelSet}
                          placeholder="Select a label set..."
                        />
                      </div>
                    )}
                  </div>
                )}

                <DeprecatedSingleSelect<BoardOption>
                  css={styles.formElement}
                  label="Board"
                  options={boardOptions}
                  menuWidth="calc(100% - 64px)"
                  isSearchable={true}
                  selectedItem={selectedBoard || undefined}
                  onChange={setSelectedBoard}
                  placeholder="Select a board..."
                />

                {selectedBoard && (
                  <div css={styles.nestedGroupingSection}>
                    <h4 css={styles.nestedGroupingTitle}>Moment Grouping</h4>
                    <div css={styles.radioGroup}>
                      <Radio.Group
                        name="boardGrouping"
                        theme={theme}
                        defaultValue={boardGroupingType}
                        onChange={(e) =>
                          setBoardGroupingType(e.target.value as GroupingType)
                        }
                      >
                        <Radio.Input
                          value="story"
                          label="Group by story name"
                        />
                        <Radio.Input
                          value="existing"
                          label="Group into existing collection"
                        />
                        <Radio.Input
                          value="labelset"
                          label="Group by label set"
                        />
                      </Radio.Group>
                    </div>

                    {boardGroupingType === 'existing' && (
                      <div css={styles.subSelection}>
                        {isCollectionOptionsLoading ? (
                          <DotLoader />
                        ) : collectionOptions.length > 0 ? (
                          <DeprecatedSingleSelect<CollectionOption>
                            css={styles.formElement}
                            label="Existing Collection"
                            options={collectionOptions}
                            menuWidth="calc(100% - 64px)"
                            isSearchable={true}
                            selectedItem={selectedCollection || undefined}
                            onChange={setSelectedCollection}
                            placeholder="Select an existing collection..."
                          />
                        ) : (
                          <p css={styles.subSelectionNote}>
                            No collections found in this board.
                          </p>
                        )}
                      </div>
                    )}

                    {boardGroupingType === 'labelset' && (
                      <div css={styles.subSelection}>
                        <DeprecatedSingleSelect<LabelSetOption>
                          css={styles.formElement}
                          label="Label Set for Board"
                          options={labelSetOptions}
                          menuWidth="calc(100% - 64px)"
                          isSearchable={true}
                          selectedItem={selectedBoardLabelSet || undefined}
                          onChange={setSelectedBoardLabelSet}
                          placeholder="Select a label set..."
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div css={styles.infoSection}>
                <p css={styles.infoText}>
                  <strong>Note:</strong> Moments will be granted access to the
                  plan or board they are migrated to.
                </p>
              </div>
            </Fragment>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            name="Cancel"
            variant="ghost"
            buttonText="Cancel"
            onClick={onCloseModal}
            disabled={isSubmitting}
          />
          <Button
            name="Migrate"
            disabled={isSubmitDisabled}
            buttonText={isSubmitting ? 'Migrating...' : 'Migrate Moments'}
            variant="primary"
            onClick={handleMigrate}
          />
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  )
}
